import React, { useState } from "react";
import { loginUser, getSubscriptionStatus } from "../../api";
import { getLoginFailureMessage } from "../../utils/loginErrorMessage";
import useGoToPricing from "../../hooks/useGoToPricing";
import Header from "../Homepage/sections/Header";
import { Page, HomepageGlobalStyles } from "../Homepage/styles";
import {
  LoginAlert,
  LoginCard,
  LoginCardHeader,
  LoginField,
  LoginFooterLink,
  LoginFooterNote,
  LoginForgotLink,
  LoginForm,
  LoginGlow,
  LoginHomeLink,
  LoginHomeWrap,
  LoginInner,
  LoginInput,
  LoginInputIcon,
  LoginInputWrap,
  LoginLabel,
  LoginLabelRow,
  LoginMain,
  LoginPage as LoginPageRoot,
  LoginShell,
  LoginSpinIcon,
  LoginSubmit,
  LoginSubtitle,
  LoginTitle,
} from "./Login.styles";

const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const LockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" x2="12" y1="8" y2="12" />
    <line x1="12" x2="12.01" y1="16" y2="16" />
  </svg>
);

const LoaderIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m12 19-7-7 7-7" />
    <path d="M19 12H5" />
  </svg>
);

/**
 * Hard navigation after login so iOS doesn't keep the scrolled login offset.
 * Destination (members vs billing) matches prod LoginForm.
 */
const redirectAfterLogin = (path) => {
  const active = document.activeElement;
  if (active && typeof active.blur === "function") {
    active.blur();
  }
  window.setTimeout(() => {
    window.location.replace(path);
  }, 350);
};

/**
 * Lovable Login shell + Homepage Header + prod loginUser / subscription redirect.
 */
const Login = () => {
  const goToPricing = useGoToPricing();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getProfile = (session) => {
    fetch(`${process.env.REACT_APP_API_URL}user/${session.userId}`, {
      headers: {
        "Content-Type": "application/json",
        "X-Auth-Token": session.token,
      },
    })
      .then((response) => response.json())
      .then(async (data) => {
        localStorage.setItem("user", JSON.stringify(data));

        if (
          typeof Notification !== "undefined" &&
          Notification.permission === "default"
        ) {
          Notification.requestPermission(() => {});
        }

        let nextPath = "/blogs/premium/";
        try {
          const { data: subscriptionData } = await getSubscriptionStatus(
            data && data.id
          );
          if (subscriptionData && subscriptionData.subscriptionStatusId === 4) {
            nextPath = "/user-profile?tab=billing";
          }
        } catch {
          // Fall through to members feed.
        }

        redirectAfterLogin(nextPath);
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const processLogin = async () => {
    setError("");
    const trimmedEmail = email.trim();

    if (!trimmedEmail && !password) {
      setError("Please enter your email and password.");
      return;
    }
    if (!trimmedEmail) {
      setError("Please enter your email address.");
      return;
    }
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setIsSubmitting(true);
    const res = await loginUser({ email: trimmedEmail, password });

    if (res && res.status >= 200 && res.status < 300) {
      localStorage.setItem("session", JSON.stringify(res.data.token));
      getProfile(res.data);
      return;
    }

    setError(getLoginFailureMessage(res));
    setIsSubmitting(false);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    processLogin();
  };

  return (
    <Page>
      <HomepageGlobalStyles />
      <LoginPageRoot>
        <LoginGlow aria-hidden="true" />
        <LoginShell>
          <Header onSubscribeClick={goToPricing} hideSpacer />

          <LoginMain>
            <LoginInner>
              <LoginCard>
                <LoginCardHeader>
                  <LoginTitle>Welcome back</LoginTitle>
                  <LoginSubtitle>
                    Log in to access your member content.
                  </LoginSubtitle>
                </LoginCardHeader>

                <LoginForm onSubmit={onSubmit} noValidate>
                  {error ? (
                    <LoginAlert role="alert">
                      <AlertCircleIcon />
                      <span>{error}</span>
                    </LoginAlert>
                  ) : null}

                  <LoginField>
                    <LoginLabel htmlFor="login-email">Email</LoginLabel>
                    <LoginInputWrap>
                      <LoginInputIcon>
                        <MailIcon />
                      </LoginInputIcon>
                      <LoginInput
                        id="login-email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </LoginInputWrap>
                  </LoginField>

                  <LoginField>
                    <LoginLabelRow>
                      <LoginLabel htmlFor="login-password">Password</LoginLabel>
                      <LoginForgotLink to="/forgot">
                        Forgot password?
                      </LoginForgotLink>
                    </LoginLabelRow>
                    <LoginInputWrap>
                      <LoginInputIcon>
                        <LockIcon />
                      </LoginInputIcon>
                      <LoginInput
                        id="login-password"
                        type="password"
                        autoComplete="current-password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </LoginInputWrap>
                  </LoginField>

                  <LoginSubmit type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <LoginSpinIcon>
                          <LoaderIcon />
                        </LoginSpinIcon>
                        Signing in…
                      </>
                    ) : (
                      "Log in"
                    )}
                  </LoginSubmit>
                </LoginForm>
              </LoginCard>

              <LoginFooterNote>
                Don&apos;t have an account?{" "}
                <LoginFooterLink to="/register">
                  Create an account
                </LoginFooterLink>
              </LoginFooterNote>

              <LoginHomeWrap>
                <LoginHomeLink to="/">
                  <ArrowLeftIcon />
                  Back to home
                </LoginHomeLink>
              </LoginHomeWrap>
            </LoginInner>
          </LoginMain>
        </LoginShell>
      </LoginPageRoot>
    </Page>
  );
};

export default Login;
